import { useState, useEffect } from "react";
import Button from "@/components/Button";
import {
  Copy,
  Check,
  ExternalLink,
  Code,
  Link2,
  Pencil,
  X,
  Loader2,
  MapPin,
  Facebook,
  Instagram,
  ChevronRight,
  Smartphone,
  Monitor,
  CheckCircle2,
  ArrowRight,
  Settings,
  Zap,
  Clock,
  Info,
  MessageSquare,
  Receipt,
} from "lucide-react";
import { useLocation } from "../contexts/LocationContext";
import apiClient from "../lib/api-client";
import { useSubscription } from "../hooks/useSubscription";
import WhatsAppUpgradeNotice from "../components/WhatsAppUpgradeNotice";
import SocialMessagingCard from "../components/SocialMessagingCard";
import TpvIntegrationCard from "../components/TpvIntegrationCard";

type TabType = "widget" | "google" | "facebook" | "instagram" | "whatsapp" | "tpv";

const WIDGET_BASE_URL =
  import.meta.env.VITE_WIDGET_URL || "https://widget.reservamanager.com";

const tabs: {
  id: TabType;
  label: string;
  icon: React.ElementType;
  color: string;
}[] = [
  { id: "widget", label: "Tu Web", icon: Code, color: "primary" },
  { id: "google", label: "Google", icon: MapPin, color: "red" },
  { id: "facebook", label: "Facebook", icon: Facebook, color: "blue" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "pink" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageSquare, color: "green" },
  { id: "tpv", label: "TPV", icon: Receipt, color: "primary" },
];

export default function Integration() {
  const { currentLocation, isLoading, refreshLocations, currentBookingMode } = useLocation();
  // Google Reserve solo funciona con reservas de mesa (el módulo escribe y lee
  // `reservation`): a un negocio de citas se le enseña solo el enlace manual.
  const isAppointmentsMode = currentBookingMode === "appointments";
  const { features } = useSubscription();

  // Flag REAL del plan (el backend ignora el trial al decidir si envía WhatsApp,
  // así que aquí se gatea por el flag crudo del plan, no por canAccessWhatsApp).
  // Mientras `features` está cargando (null) se trata como neutro para evitar
  // parpadeo: no se pinta bloqueado hasta saber con certeza que el plan no lo incluye.
  // Mismo criterio que pages/Notifications.tsx.
  const whatsappBlocked = features !== null && features.hasWhatsApp === false;
  // El TPV lee reservas de mesa: a un negocio de citas no se le ofrece.
  const visibleTabs = isAppointmentsMode ? tabs.filter((tab) => tab.id !== "tpv") : tabs;
  const [copiedWidget, setCopiedWidget] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedWaLink, setCopiedWaLink] = useState(false);
  // Al volver de Facebook tras conectar Instagram y Messenger, el servidor
  // redirige aquí con el resultado en la URL (`social…`): se abre directamente
  // la pestaña donde está esa tarjeta (ver `SocialMessagingCard`).
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    /[?&]social(_error|_pending)?=/.test(window.location.search) ? "instagram" : "widget",
  );

  // Slug editing state
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [editedSlug, setEditedSlug] = useState("");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [isSavingSlug, setIsSavingSlug] = useState(false);

  // Notes placeholder state
  const [notesPlaceholder, setNotesPlaceholder] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Email reminder state — movido a Notifications.tsx

  // Google Reserve state
  const [googleOption, setGoogleOption] = useState<"manual" | "reserve">(
    "manual",
  );
  const [googleReserveEnabled, setGoogleReserveEnabled] = useState(false);
  const [googlePlaceId, setGooglePlaceId] = useState("");
  const [isSavingGoogle, setIsSavingGoogle] = useState(false);
  const [googleSuccess, setGoogleSuccess] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  // WhatsApp state (mismo flag `whatsappEnabled` que Ajustes del local / Notificaciones)
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [isSavingWhatsapp, setIsSavingWhatsapp] = useState(false);
  const [whatsappSuccess, setWhatsappSuccess] = useState(false);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);

  // Initialize edited slug when location changes
  useEffect(() => {
    if (currentLocation) {
      setEditedSlug(currentLocation.slug);
      // Load Google Reserve settings
      setGoogleReserveEnabled(
        (currentLocation as any).googleReserveEnabled || false,
      );
      setGooglePlaceId((currentLocation as any).googlePlaceId || "");
      setWhatsappEnabled(currentLocation.whatsappEnabled ?? false);
      // Load notes placeholder from schedule config
      apiClient
        .get(`/schedule/config`)
        .then((res) => {
          const cfg = res.data?.config;
          setNotesPlaceholder(cfg?.notesPlaceholder || "");
        })
        .catch(() => {});
    }
  }, [currentLocation]);

  const widgetUrl = currentLocation
    ? `${WIDGET_BASE_URL}/${currentLocation.slug}`
    : "";

  const widgetCode = currentLocation
    ? `<!-- Widget de Reservas - ${currentLocation.name} -->
<script src="${WIDGET_BASE_URL}/widget.js"></script>
<reserva-widget 
  restaurant-slug="${currentLocation.slug}"
  height="800">
</reserva-widget>`
    : "";

  const copyToClipboard = async (text: string, type: "widget" | "url") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "widget") {
        setCopiedWidget(true);
        setTimeout(() => setCopiedWidget(false), 2000);
      } else {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }
    } catch (err) {
      console.error("Error copying to clipboard:", err);
    }
  };

  const handleEditSlug = () => {
    setEditedSlug(currentLocation?.slug || "");
    setSlugError(null);
    setIsEditingSlug(true);
  };

  const handleCancelEdit = () => {
    setEditedSlug(currentLocation?.slug || "");
    setSlugError(null);
    setIsEditingSlug(false);
  };

  const handleSaveSlug = async () => {
    if (!currentLocation) return;

    // Normalize slug
    const normalizedSlug = editedSlug
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

    if (normalizedSlug.length < 3) {
      setSlugError("El slug debe tener al menos 3 caracteres");
      return;
    }

    if (normalizedSlug === currentLocation.slug) {
      setIsEditingSlug(false);
      return;
    }

    try {
      setIsSavingSlug(true);
      setSlugError(null);

      await apiClient.put(`/locations/${currentLocation.id}`, {
        slug: normalizedSlug,
      });

      // Refresh locations to update the context with new slug
      await refreshLocations();
      setIsEditingSlug(false);
    } catch (err) {
      console.error("Error updating slug:", err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      const errorMessage =
        axiosError.response?.data?.message || "Error al actualizar el slug";
      setSlugError(errorMessage);
    } finally {
      setIsSavingSlug(false);
    }
  };

  const handleSlugKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveSlug();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleSaveGoogleReserve = async () => {
    if (!currentLocation) return;

    try {
      setIsSavingGoogle(true);
      setGoogleError(null);

      await apiClient.put(`/locations/${currentLocation.id}`, {
        googleReserveEnabled,
        googlePlaceId: googlePlaceId || null,
      });

      await refreshLocations();
      setGoogleSuccess(true);
      setTimeout(() => setGoogleSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving Google Reserve settings:", err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      setGoogleError(
        axiosError.response?.data?.message ||
          "Error al guardar la configuración",
      );
    } finally {
      setIsSavingGoogle(false);
    }
  };

  const handleToggleWhatsapp = async () => {
    if (!currentLocation || whatsappBlocked) return;
    const nextValue = !whatsappEnabled;
    setWhatsappEnabled(nextValue);
    setIsSavingWhatsapp(true);
    setWhatsappError(null);
    try {
      await apiClient.put(`/locations/${currentLocation.id}`, {
        whatsappEnabled: nextValue,
      });
      await refreshLocations();
      setWhatsappSuccess(true);
      setTimeout(() => setWhatsappSuccess(false), 3000);
    } catch (err) {
      console.error("Error updating WhatsApp toggle:", err);
      // Revertir el toggle si falla el guardado
      setWhatsappEnabled(!nextValue);
      const axiosError = err as { response?: { data?: { message?: string } } };
      setWhatsappError(
        axiosError.response?.data?.message ||
          "Error al actualizar la configuración de WhatsApp",
      );
    } finally {
      setIsSavingWhatsapp(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentLocation) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No hay restaurante seleccionado</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Integración
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Configura dónde recibir reservas para{" "}
          <span className="font-medium">{currentLocation.name}</span>
        </p>
      </div>

      {/* URL Card - Always visible */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl shadow-lg p-4 md:p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Link2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-primary-100">Tu enlace de reservas</p>
              <p className="font-mono text-sm md:text-base truncate max-w-[250px] md:max-w-none">
                {widgetUrl}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => copyToClipboard(widgetUrl, "url")}
              className="flex-1 md:flex-none bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              {copiedUrl ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copiar
                </>
              )}
            </Button>
            <a
              href={widgetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 md:flex-none inline-flex items-center justify-center gap-1 px-4 py-2 bg-white text-primary-700 rounded-lg hover:bg-primary-50 transition-colors text-sm font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              Ver
            </a>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav
            className="flex overflow-x-auto scrollbar-hide"
            aria-label="Tabs"
          >
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? tab.id === "google"
                        ? "border-red-500 text-red-600"
                        : tab.id === "facebook"
                          ? "border-blue-600 text-blue-600"
                          : tab.id === "instagram"
                            ? "border-pink-500 text-pink-600"
                            : tab.id === "whatsapp"
                              ? "border-green-500 text-green-600"
                              : "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4 md:p-6">
          {/* Widget Tab */}
          {activeTab === "widget" && (
            <div className="space-y-6">
              {/* Slug Configuration */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-primary-600" />
                  URL Personalizada
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Personaliza el identificador único de tu restaurante en la
                  URL.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <div className="flex-1 flex items-center min-w-0">
                    <span className="hidden md:block px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-500 text-sm whitespace-nowrap">
                      {WIDGET_BASE_URL}/
                    </span>
                    {isEditingSlug ? (
                      <input
                        type="text"
                        value={editedSlug}
                        onChange={(e) => setEditedSlug(e.target.value)}
                        onKeyDown={handleSlugKeyDown}
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg md:rounded-l-none md:rounded-r-lg text-gray-700 font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="mi-restaurante"
                        autoFocus
                      />
                    ) : (
                      <span className="flex-1 min-w-0 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg md:rounded-l-none md:rounded-r-lg text-gray-700 font-mono text-sm truncate">
                        {currentLocation.slug}
                      </span>
                    )}
                  </div>
                  {isEditingSlug ? (
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        onClick={handleSaveSlug}
                        disabled={isSavingSlug}
                        className="flex-1 sm:flex-none"
                      >
                        {isSavingSlug ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        <span className="ml-1">Guardar</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancelEdit}
                        disabled={isSavingSlug}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={handleEditSlug}>
                      <Pencil className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  )}
                </div>
                {slugError && (
                  <p className="mt-2 text-sm text-red-600">{slugError}</p>
                )}
              </div>

              <hr className="border-gray-200" />

              {/* Notes Placeholder */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary-600" />
                  Texto del campo de comentarios
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Personaliza el texto de ejemplo que ven los clientes en el
                  campo de comentarios al hacer una reserva.
                </p>
                <div className="space-y-3">
                  <textarea
                    value={notesPlaceholder}
                    onChange={(e) => setNotesPlaceholder(e.target.value)}
                    rows={2}
                    maxLength={200}
                    placeholder="Reservar platos, es un cumpleaños, sillita de bebé, movilidad reducida, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-400">
                      {notesPlaceholder.length}/200 caracteres. Si se deja vacío
                      se usa el texto por defecto.
                    </p>
                    <button
                      type="button"
                      disabled={isSavingNotes}
                      onClick={async () => {
                        try {
                          setIsSavingNotes(true);
                          setNotesError(null);
                          await apiClient.put("/schedule/config", {
                            notesPlaceholder: notesPlaceholder.trim() || null,
                          });
                          setNotesSaved(true);
                          setTimeout(() => setNotesSaved(false), 2500);
                        } catch {
                          setNotesError(
                            "Error al guardar. Inténtalo de nuevo.",
                          );
                        } finally {
                          setIsSavingNotes(false);
                        }
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                      {isSavingNotes ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : notesSaved ? (
                        <Check className="w-4 h-4" />
                      ) : null}
                      {notesSaved ? "Guardado" : "Guardar"}
                    </button>
                  </div>
                  {notesError && (
                    <p className="text-sm text-red-600">{notesError}</p>
                  )}
                </div>
              </div>

              <hr className="border-gray-200" />

              {/* Embed Code */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Code className="w-5 h-5 text-primary-600" />
                  Código para tu Web
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Copia este código HTML y pégalo en tu página web donde quieras
                  mostrar el widget.
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs font-mono whitespace-pre-wrap break-all">
                    <code>{widgetCode}</code>
                  </pre>
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(widgetCode, "widget")}
                    className="absolute top-2 right-2 bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700 text-xs px-2 py-1"
                  >
                    {copiedWidget ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <hr className="border-gray-200" />

              {/* Preview Options */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-primary-600" />
                  Así se verá
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a
                    href={widgetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors group"
                  >
                    <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-primary-100">
                      <Monitor className="w-5 h-5 text-gray-600 group-hover:text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Vista Desktop</p>
                      <p className="text-sm text-gray-500">
                        Ver en pantalla completa
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
                  </a>
                  <a
                    href={widgetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors group"
                  >
                    <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-primary-100">
                      <Smartphone className="w-5 h-5 text-gray-600 group-hover:text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Vista Móvil</p>
                      <p className="text-sm text-gray-500">
                        Escanea el QR o abre en móvil
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Google Tab */}
          {activeTab === "google" && (
            <div className="space-y-6">
              {/* Option Selector (solo mesas: Google Reserve no existe para citas) */}
              {!isAppointmentsMode && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Manual Option */}
                <button
                  onClick={() => setGoogleOption("manual")}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    googleOption === "manual"
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className={`p-2 rounded-lg ${googleOption === "manual" ? "bg-red-100" : "bg-gray-100"}`}
                    >
                      <Link2
                        className={`w-5 h-5 ${googleOption === "manual" ? "text-red-600" : "text-gray-600"}`}
                      />
                    </div>
                    <h4 className="font-semibold text-gray-900">Manual</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Añade manualmente el enlace de tu widget a Google Business
                    Profile
                  </p>
                </button>

                {/* Google Reserve Option */}
                <button
                  onClick={() => setGoogleOption("reserve")}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    googleOption === "reserve"
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className={`p-2 rounded-lg ${googleOption === "reserve" ? "bg-red-100" : "bg-gray-100"}`}
                    >
                      <Zap
                        className={`w-5 h-5 ${googleOption === "reserve" ? "text-red-600" : "text-gray-600"}`}
                      />
                    </div>
                    <h4 className="font-semibold text-gray-900">
                      Google Reserve
                    </h4>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      Recomendado
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Integración nativa con Reserve with Google (botón directo en
                    Google)
                  </p>
                </button>
              </div>
              )}

              {/* Manual Option Content */}
              {(isAppointmentsMode || googleOption === "manual") && (
                <div className="space-y-6 pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-red-900">
                        Google Business Profile
                      </h3>
                      <p className="text-sm text-red-700 mt-1">
                        Permite que tus clientes reserven directamente desde
                        Google Maps y la búsqueda de Google.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 bg-red-100 text-red-600 rounded-full text-sm font-bold">
                        1
                      </span>
                      Accede a tu perfil de Google Business
                    </h4>
                    <div className="ml-8 space-y-3">
                      <p className="text-sm text-gray-600">
                        Ve a{" "}
                        <a
                          href="https://business.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:underline font-medium"
                        >
                          business.google.com
                        </a>{" "}
                        e inicia sesión con la cuenta de Google de tu
                        restaurante.
                      </p>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-500">
                          💡 Si no tienes tu negocio en Google, primero deberás
                          verificarlo. El proceso puede tardar unos días.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 bg-red-100 text-red-600 rounded-full text-sm font-bold">
                        2
                      </span>
                      Configura el botón de reservas
                    </h4>
                    <div className="ml-8 space-y-3">
                      <ol className="list-decimal list-inside text-sm text-gray-600 space-y-2">
                        <li>
                          Haz clic en <strong>"Editar perfil"</strong> en tu
                          panel de Google Business
                        </li>
                        <li>
                          Busca la sección <strong>"Reservas"</strong> o{" "}
                          <strong>"Enlaces"</strong>
                        </li>
                        <li>
                          Selecciona <strong>"Añadir enlace de reserva"</strong>
                        </li>
                        <li>Pega tu URL de reservas:</li>
                      </ol>
                      <div className="flex items-center gap-2 bg-gray-100 p-3 rounded-lg">
                        <input
                          type="text"
                          readOnly
                          value={widgetUrl}
                          className="flex-1 bg-transparent text-sm font-mono text-gray-700 outline-none truncate"
                        />
                        <Button
                          variant="outline"
                          onClick={() => copyToClipboard(widgetUrl, "url")}
                          className="flex-shrink-0 text-xs"
                        >
                          {copiedUrl ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <ol
                        start={5}
                        className="list-decimal list-inside text-sm text-gray-600 space-y-2"
                      >
                        <li>Guarda los cambios</li>
                      </ol>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 bg-red-100 text-red-600 rounded-full text-sm font-bold">
                        3
                      </span>
                      ¡Listo! Así aparecerá
                    </h4>
                    <div className="ml-8">
                      <div className="bg-white border border-gray-300 rounded-xl p-4 max-w-sm shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {currentLocation.name}
                            </p>
                            <p className="text-xs text-gray-500">Restaurante</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button className="flex-1 bg-blue-600 text-white text-sm py-2 px-4 rounded-full font-medium hover:bg-blue-700">
                            Reservar
                          </button>
                          <button className="flex-1 border border-gray-300 text-sm py-2 px-4 rounded-full font-medium hover:bg-gray-50">
                            Llamar
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Vista previa aproximada del botón en Google
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Google Reserve Option Content */}
              {!isAppointmentsMode && googleOption === "reserve" && (
                <div className="space-y-6 pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg">
                    <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                      <Zap className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-red-900">
                        Reserve with Google
                      </h3>
                      <p className="text-sm text-red-700 mt-1">
                        Integración directa con Google. Los clientes reservan
                        sin salir de Google Maps con el botón "Reservar mesa".
                      </p>
                    </div>
                  </div>

                  {/* Configuration Form */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-gray-600" />
                      Configuración
                    </h4>

                    {/* Enable Toggle */}
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                      <div>
                        <p className="font-medium text-gray-900">
                          Activar Google Reserve
                        </p>
                        <p className="text-sm text-gray-500">
                          Tu restaurante aparecerá en el programa Reserve with
                          Google
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          setGoogleReserveEnabled(!googleReserveEnabled)
                        }
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                          googleReserveEnabled ? "bg-red-600" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            googleReserveEnabled
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Place ID */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Google Place ID
                      </label>
                      <input
                        type="text"
                        value={googlePlaceId}
                        onChange={(e) => setGooglePlaceId(e.target.value)}
                        placeholder="ChIJ..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Obtén tu Place ID en{" "}
                        <a
                          href="https://developers.google.com/maps/documentation/places/web-service/place-id"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:underline"
                        >
                          Google Place ID Finder
                        </a>
                      </p>
                    </div>

                    {/* Save Button */}
                    <div className="flex items-center gap-3 pt-2">
                      <Button
                        variant="primary"
                        onClick={handleSaveGoogleReserve}
                        disabled={isSavingGoogle}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {isSavingGoogle ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Guardar configuración
                      </Button>
                      {googleSuccess && (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          Guardado correctamente
                        </span>
                      )}
                      {googleError && (
                        <span className="text-sm text-red-600">
                          {googleError}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status Banner */}
                  {googleReserveEnabled ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-green-900">
                            Google Reserve está activo
                          </p>
                          <p className="text-sm text-green-700 mt-1">
                            Tu restaurante aparecerá en el feed de Reserve with
                            Google una vez que Google procese tus datos.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-900">
                            Pendiente de activación
                          </p>
                          <p className="text-sm text-yellow-700 mt-1">
                            Activa Google Reserve y añade tu Place ID para
                            comenzar a recibir reservas desde Google.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-900">
                          Ventajas de Google Reserve
                        </p>
                        <ul className="text-sm text-red-700 mt-2 space-y-1">
                          <li>
                            • Botón "Reservar mesa" directo en Google Maps
                          </li>
                          <li>• Los clientes reservan sin salir de Google</li>
                          <li>• Sincronización automática de disponibilidad</li>
                          <li>• Mayor conversión que enlaces externos</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Common Benefits Section */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-900">
                      Beneficios de integrar con Google
                    </p>
                    <ul className="text-sm text-green-700 mt-2 space-y-1">
                      <li>
                        • Aparece el botón "Reservar" en tu ficha de Google Maps
                      </li>
                      <li>
                        • Tus clientes pueden reservar sin salir de Google
                      </li>
                      <li>• Mayor visibilidad en búsquedas locales</li>
                      <li>• Reservas identificadas como fuente "Google"</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Facebook Tab */}
          {activeTab === "facebook" && (
            <div className="space-y-6">
              {currentLocation && (
                <SocialMessagingCard
                  locationId={currentLocation.id}
                  locationName={currentLocation.name}
                />
              )}
              <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <Facebook className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">
                    Página de Facebook
                  </h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Añade un botón de acción "Reservar" en tu página de Facebook
                    para que tus seguidores puedan reservar fácilmente.
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-sm font-bold">
                    1
                  </span>
                  Accede a tu página de Facebook
                </h4>
                <div className="ml-8 space-y-3">
                  <p className="text-sm text-gray-600">
                    Ve a{" "}
                    <a
                      href="https://www.facebook.com/pages"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-medium"
                    >
                      facebook.com/pages
                    </a>{" "}
                    y selecciona la página de tu restaurante.
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-sm font-bold">
                    2
                  </span>
                  Configura el botón de acción
                </h4>
                <div className="ml-8 space-y-3">
                  <ol className="list-decimal list-inside text-sm text-gray-600 space-y-2">
                    <li>
                      Haz clic en el botón <strong>"Añadir un botón"</strong> o{" "}
                      <strong>"Editar botón de acción"</strong> debajo de la
                      portada
                    </li>
                    <li>
                      Selecciona la categoría <strong>"Reservar"</strong> o{" "}
                      <strong>"Hacer una reserva"</strong>
                    </li>
                    <li>
                      Elige <strong>"Enlace a sitio web"</strong>
                    </li>
                    <li>Pega tu URL de reservas:</li>
                  </ol>
                  <div className="flex items-center gap-2 bg-gray-100 p-3 rounded-lg">
                    <input
                      type="text"
                      readOnly
                      value={widgetUrl}
                      className="flex-1 bg-transparent text-sm font-mono text-gray-700 outline-none truncate"
                    />
                    <Button
                      variant="outline"
                      onClick={() => copyToClipboard(widgetUrl, "url")}
                      className="flex-shrink-0 text-xs"
                    >
                      {copiedUrl ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <ol
                    start={5}
                    className="list-decimal list-inside text-sm text-gray-600 space-y-2"
                  >
                    <li>
                      Haz clic en <strong>"Guardar"</strong>
                    </li>
                  </ol>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-sm font-bold">
                    3
                  </span>
                  ¡Listo! Así aparecerá
                </h4>
                <div className="ml-8">
                  <div className="bg-white border border-gray-300 rounded-xl overflow-hidden max-w-sm shadow-sm">
                    <div className="h-20 bg-gradient-to-r from-blue-400 to-blue-600"></div>
                    <div className="p-4">
                      <div className="flex items-end gap-3 -mt-10 mb-3">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg border-4 border-white"></div>
                        <div className="flex-1 pt-6">
                          <p className="font-bold text-gray-900">
                            {currentLocation.name}
                          </p>
                          <p className="text-xs text-gray-500">Restaurante</p>
                        </div>
                      </div>
                      <button className="w-full bg-blue-600 text-white text-sm py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
                        Reservar
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Vista previa aproximada del botón en Facebook
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">
                      Beneficios de integrar con Facebook
                    </p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1">
                      <li>• Botón prominente "Reservar" en tu página</li>
                      <li>• Tus seguidores pueden reservar con un clic</li>
                      <li>• Funciona en desktop y móvil</li>
                      <li>• Reservas identificadas como fuente "Facebook"</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Instagram Tab */}
          {activeTab === "instagram" && (
            <div className="space-y-6">
              {currentLocation && (
                <SocialMessagingCard
                  locationId={currentLocation.id}
                  locationName={currentLocation.name}
                />
              )}
              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg">
                <div className="p-2 bg-gradient-to-br from-pink-100 to-purple-100 rounded-lg flex-shrink-0">
                  <Instagram className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-pink-900">
                    Perfil de Instagram
                  </h3>
                  <p className="text-sm text-pink-700 mt-1">
                    Añade el enlace de reservas en tu bio de Instagram y como
                    botón de acción en cuentas profesionales.
                  </p>
                </div>
              </div>

              {/* Option A: Bio Link */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">
                  Opción A: Enlace en la Bio
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-pink-100 text-pink-600 rounded-full text-sm font-bold flex-shrink-0">
                      1
                    </span>
                    <div>
                      <p className="text-sm text-gray-600">
                        Abre Instagram y ve a tu perfil
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-pink-100 text-pink-600 rounded-full text-sm font-bold flex-shrink-0">
                      2
                    </span>
                    <div>
                      <p className="text-sm text-gray-600">
                        Toca <strong>"Editar perfil"</strong>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-pink-100 text-pink-600 rounded-full text-sm font-bold flex-shrink-0">
                      3
                    </span>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">
                        En el campo <strong>"Sitio web"</strong>, pega tu URL:
                      </p>
                      <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-gray-200">
                        <input
                          type="text"
                          readOnly
                          value={widgetUrl}
                          className="flex-1 bg-transparent text-sm font-mono text-gray-700 outline-none truncate"
                        />
                        <Button
                          variant="outline"
                          onClick={() => copyToClipboard(widgetUrl, "url")}
                          className="flex-shrink-0 text-xs"
                        >
                          {copiedUrl ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-pink-100 text-pink-600 rounded-full text-sm font-bold flex-shrink-0">
                      4
                    </span>
                    <div>
                      <p className="text-sm text-gray-600">
                        Guarda los cambios
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Option B: Action Button */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Opción B: Botón de Reservar (Cuenta Profesional)
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Requiere cuenta de empresa o creador
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-600 rounded-full text-sm font-bold flex-shrink-0">
                      1
                    </span>
                    <p className="text-sm text-gray-600">
                      Ve a{" "}
                      <strong>
                        Configuración → Empresa → Botones de acción
                      </strong>
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-600 rounded-full text-sm font-bold flex-shrink-0">
                      2
                    </span>
                    <p className="text-sm text-gray-600">
                      Selecciona <strong>"Reservar"</strong>
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-600 rounded-full text-sm font-bold flex-shrink-0">
                      3
                    </span>
                    <p className="text-sm text-gray-600">
                      Elige <strong>"Añadir URL de reservas"</strong> y pega tu
                      enlace
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-4">
                  Así se verá tu perfil
                </h4>
                <div className="flex justify-center">
                  <div className="bg-white border border-gray-300 rounded-2xl p-4 w-72 shadow-sm">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-20 h-20 bg-gradient-to-br from-pink-400 via-purple-400 to-yellow-400 rounded-full p-0.5">
                        <div className="w-full h-full bg-gray-200 rounded-full"></div>
                      </div>
                      <div className="flex-1 grid grid-cols-3 text-center gap-2">
                        <div>
                          <p className="font-bold text-gray-900">124</p>
                          <p className="text-xs text-gray-500">posts</p>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">2.4K</p>
                          <p className="text-xs text-gray-500">seguidores</p>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">186</p>
                          <p className="text-xs text-gray-500">seguidos</p>
                        </div>
                      </div>
                    </div>
                    <p className="font-bold text-gray-900 text-sm">
                      {currentLocation.name}
                    </p>
                    <p className="text-xs text-gray-500 mb-1">Restaurante</p>
                    <p className="text-xs text-gray-700 mb-2">
                      📍 Los mejores platos de la ciudad 🍽️
                    </p>
                    <a className="text-xs text-blue-600 font-medium block mb-3 truncate">
                      {widgetUrl.replace("https://", "")}
                    </a>
                    <div className="grid grid-cols-2 gap-2">
                      <button className="bg-gray-100 text-gray-900 text-xs py-1.5 px-3 rounded-lg font-medium">
                        Seguir
                      </button>
                      <button className="bg-pink-500 text-white text-xs py-1.5 px-3 rounded-lg font-medium">
                        Reservar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-pink-900">
                      Beneficios de integrar con Instagram
                    </p>
                    <ul className="text-sm text-pink-700 mt-2 space-y-1">
                      <li>• Enlace visible para todos tus seguidores</li>
                      <li>• Reservas directas desde la app de Instagram</li>
                      <li>• Menciona el link en tus stories e historias</li>
                      <li>• Reservas identificadas como fuente "Instagram"</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp Tab */}
          {activeTab === "tpv" && !isAppointmentsMode && (
            <TpvIntegrationCard
              locationId={currentLocation.id}
              locationName={currentLocation.name}
            />
          )}

          {activeTab === "whatsapp" && (
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-900">
                    WhatsApp Business
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    Envía confirmaciones y recordatorios de reserva a tus
                    clientes directamente por WhatsApp.
                  </p>
                </div>
              </div>

              {/* Enlace wa.me de la sede con WhatsApp IA: para Google Business
                  Profile (Contacto → Chat), Instagram, etc. Solo con número
                  aprovisionado — ahí contesta Carmen. */}
              {currentLocation.whatsappPhoneNumber &&
                currentLocation.whatsappPhoneNumberId && (
                  <div className="bg-white rounded-lg border border-green-200 p-4 space-y-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      Tu enlace de chat con IA (WhatsApp)
                    </h4>
                    <p className="text-sm text-gray-600">
                      Cualquiera que abra este enlace empieza un chat con{" "}
                      <strong>Carmen</strong> en el WhatsApp de tu negocio (
                      {currentLocation.whatsappPhoneNumber}): reservas, dudas y
                      cambios atendidos al momento, 24/7.
                    </p>
                    {(() => {
                      const waLink = `https://wa.me/${(currentLocation.whatsappPhoneNumber || "").replace(/\D/g, "")}`;
                      return (
                        <div className="flex items-center gap-2">
                          <code className="flex-1 min-w-0 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 truncate">
                            {waLink}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(waLink);
                              setCopiedWaLink(true);
                              setTimeout(() => setCopiedWaLink(false), 2000);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0"
                          >
                            {copiedWaLink ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                            {copiedWaLink ? "Copiado" : "Copiar"}
                          </button>
                        </div>
                      );
                    })()}
                    <p className="text-xs text-gray-500">
                      Dónde pegarlo: <strong>Perfil de Empresa de Google</strong>{" "}
                      (Editar perfil → Contacto → Chat → WhatsApp → "URL de clic
                      para chatear"), enlace de la bio de Instagram, Facebook, tu
                      web o cualquier campaña.
                    </p>
                  </div>
                )}

              {/* Aviso de plan de pago requerido */}
              {whatsappBlocked && <WhatsAppUpgradeNotice />}

              {/* Enable Toggle */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-600" />
                  Configuración para {currentLocation.name}
                </h4>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div>
                    <p className="font-medium text-gray-900">
                      Activar envío de WhatsApp a clientes en este local
                    </p>
                    <p className="text-sm text-gray-500">
                      {whatsappBlocked
                        ? "Bloqueado: tu plan actual no incluye WhatsApp."
                        : whatsappEnabled
                          ? "Se enviarán confirmaciones/recordatorios por WhatsApp a los clientes de este local."
                          : "No se enviará ningún WhatsApp a los clientes de este local."}
                    </p>
                  </div>
                  <button
                    onClick={handleToggleWhatsapp}
                    disabled={isSavingWhatsapp || whatsappBlocked}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      !whatsappBlocked && whatsappEnabled
                        ? "bg-green-600"
                        : "bg-gray-200"
                    } ${
                      isSavingWhatsapp || whatsappBlocked
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        !whatsappBlocked && whatsappEnabled
                          ? "translate-x-5"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                <p className="text-xs text-gray-500 flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  Aunque las credenciales de WhatsApp Business ya estén
                  conectadas a la plataforma, si este interruptor está
                  desactivado no se enviará ningún WhatsApp de confirmación ni
                  recordatorio a los clientes de este local.
                </p>

                {whatsappSuccess && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    Guardado correctamente
                  </span>
                )}
                {whatsappError && (
                  <span className="text-sm text-red-600 block">
                    {whatsappError}
                  </span>
                )}
              </div>

              {/* Status Banner (oculto mientras está bloqueado por plan: el
                  aviso ámbar de arriba ya explica el estado real) */}
              {!whatsappBlocked &&
                (whatsappEnabled ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-900">
                          El envío de WhatsApp está activo en este local
                        </p>
                        <p className="text-sm text-green-700 mt-1">
                          Puedes ajustar los recordatorios (cuántas horas
                          antes enviarlos) en{" "}
                          <span className="font-medium">Notificaciones</span>.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-900">
                          WhatsApp desactivado en este local
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          Activa el interruptor de arriba para empezar a
                          enviar confirmaciones por WhatsApp a tus clientes.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

              {/* Benefits */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-900">
                      Ventajas de las notificaciones por WhatsApp
                    </p>
                    <ul className="text-sm text-green-700 mt-2 space-y-1">
                      <li>• Mayor tasa de apertura que el email</li>
                      <li>
                        • El cliente recibe el código de confirmación al
                        instante
                      </li>
                      <li>
                        • Recordatorios automáticos que reducen las
                        no-shows
                      </li>
                      <li>• Requiere que el cliente acepte notificaciones</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
